/**
 * A host adapter that answers the way Encompass is documented to, for local development and for
 * the Playwright smoke test. It is loaded only when the page is opened with ?stubHost=1, so it
 * cannot reach a deployed iframe by accident.
 *
 * Keep it honest: if the real adapter turns out to behave differently, change BOTH this and
 * host-adapter.js. A stub that has drifted from the thing it stands in for is worse than none —
 * which is not hypothetical: this stub spoke the v1 shape (connect() returning the connection)
 * until 2026-09-30, host-adapter.js was written against the stub, and the first real-host launch
 * timed out because the REAL v2 host speaks getObject. This is now the v2 shape: connect() is a
 * ready signal, getObject('transaction') / getObject('application') carry the API.
 */
export function installStubHostAdapter({ delayMs = 0, transaction = null } = {}) {
  let session = 0;

  const transactionObject = {
    getOrigin: async () => ({
      id: 'stub-origin-1',
      partnerAccessToken: `STUB-PAT-${session}`,
      // v2: the origin itself says which transaction the frame opened in, when there is one.
      ...(transaction?.id ? { transactionId: transaction.id } : {}),
      context: 'lender',
    }),
    // transaction.refreshOrigin: a fresh partner access token for the same origin.
    refreshOrigin: async () => ({ id: 'stub-origin-1', partnerAccessToken: `STUB-PAT-${++session}` }),
    // transaction.get: only meaningful inside an existing transaction context, like the real one.
    get: async () => {
      if (!transaction) throw { message: 'No transaction in this context.' };
      return transaction;
    },
    // transaction.create: the host mints the transaction and hands back its id, nothing more.
    // The order number is our backend's and arrives later, over ICE's webhook.
    create: async () => ({ id: `stub-tx-${Math.random().toString(16).slice(2, 8)}` }),
    // transaction.set({ id }): the host is told which transaction the iframe is showing.
    set: async () => {},
    // transaction.close / transaction.cancel: the mock's 600 / 500 ms round trips.
    close: async () => { await new Promise((r) => setTimeout(r, 600)); },
    cancel: async () => { await new Promise((r) => setTimeout(r, 500)); },
  };

  const applicationObject = {
    // What the host says it supports; host-adapter picks the picker's action ID from this.
    getCapabilities: () => ({ supportedActions: ['getAvailableResources', 'updateCredentials'] }),
    // application.performAction: v2's home for updateCredentials AND for getAvailableResources.
    // In Encompass that action OPENS the eFolder picker and resolves to what the officer chose;
    // the stub stands in for an officer who picks these three.
    performAction: async (action, options) => {
      if (action === 'getAvailableResources') {
        // &docs=0: the officer closes the dialog without choosing anything -- the real host
        // resolves [] for that, and ordering must still be possible (document-picker's `valid`).
        if (new URLSearchParams(globalThis.location?.search ?? '').get('docs') === '0') return [];
        void options; // the real host honours allowedSourceTypes & co; the stub has one eFolder
        return [
          { id: 'doc-1', repository: 'urn:elli:skydrive', name: 'Loan Application.pdf', mimeType: 'application/pdf', type: 'Application', dateUtc: '2026-08-30T00:00:00Z' },
          { id: 'doc-2', repository: 'urn:elli:skydrive', name: 'Title Commitment.pdf', mimeType: 'application/pdf', type: 'Title', dateUtc: '2026-08-31T00:00:00Z' },
          { id: 'doc-3', repository: 'urn:elli:skydrive', name: 'Closing Instructions.pdf', mimeType: 'application/pdf', type: 'Instructions', dateUtc: '2026-09-01T00:00:00Z' },
        ];
      }
      return undefined;
    },
  };

  globalThis.elli = {
    script: {
      // The ready signal. The real host drops its launch spinner on receiving it; the stub has
      // nothing to drop, so it only records that the app behaved.
      connect: () => {},
      getObject: async (name) => {
        if (delayMs) await new Promise((r) => setTimeout(r, delayMs));
        if (name === 'transaction') return transactionObject;
        if (name === 'application') return applicationObject;
        throw { message: `Unknown scripting object '${name}'.` };
      },
    },
  };
}
